import * as React from "react"
import {
  ArrowLeftRightIcon,
  HandCoinsIcon,
  HeartHandshakeIcon,
  PieChartIcon,
  ReceiptTextIcon,
  ScaleIcon,
  type LucideIcon,
} from "lucide-react"

import { EmptyState } from "@ibada/ui"

import { FeatureFlag } from "@ibada/shared-types/feature-flags"

import { useTranslations } from "@/i18n"
import { useFeatureFlag } from "@/shared/feature-flags"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"

import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"

interface QuickAction {
  key: string
  icon: LucideIcon
  path: string
  show: boolean
}

/**
 * Quick Actions — fast access to common Finance work. Pure navigation: it holds no data query (so no
 * Refresh/View-details menu items), and shows only actions the current user may perform (module edit
 * permissions + branch/parish context). It duplicates no business rules — it just routes to existing flows.
 */
export function QuickActionsWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  const finance = useModulePermission(WorkspaceAccessModule.FINANCE)
  const budgeting = useModulePermission(WorkspaceAccessModule.BUDGETING)
  // Paused for V1. A shortcut into a hidden feature routes to a screen the user cannot reach — permission
  // to do a thing is not the same as the thing being switched on.
  const budgetingEnabled = useFeatureFlag(FeatureFlag.Budgeting)
  const pledgesEnabled = useFeatureFlag(FeatureFlag.Pledges)
  const isParish = scope.context === "parish"

  /*
   * `scope.canEdit` is the gate, not the module permission alone.
   *
   * A module permission says what this person may do somewhere; `scope.canEdit` says whether THIS board is a
   * place to do it. They come apart in the Branch Details console: a parish administrator inspecting
   * Kisangani holds finance edit rights of their own, and every button here would have opened a write flow
   * scoped to Kisangani — a branch they are reading, not working in. The scope is read-only there
   * (finance-overview-context.tsx sets canEdit false while inspecting) and on the consolidated board, where
   * there is no single branch to post into at all.
   */
  /*
   * Asked per destination, not once for the widget: these are create routes, and while inspecting a branch
   * `scope.canEdit` is already false so none of them render — but if that ever changes, each action still has
   * to prove its own destination can be reached honestly.
   */
  const canAct = scope.canEdit

  const actions = React.useMemo<QuickAction[]>(
    () =>
      [
        { key: "income", icon: HandCoinsIcon, path: OverviewRoutes.createIncome, show: finance.canEdit },
        { key: "expense", icon: ReceiptTextIcon, path: OverviewRoutes.createExpense, show: finance.canEdit },
        { key: "transfer", icon: ArrowLeftRightIcon, path: OverviewRoutes.createTransfer, show: finance.canEdit },
        { key: "pledge", icon: HeartHandshakeIcon, path: OverviewRoutes.createPledge, show: pledgesEnabled && finance.canEdit && !isParish },
        { key: "reconcile", icon: ScaleIcon, path: OverviewRoutes.reconciliation, show: finance.canEdit },
        { key: "budget", icon: PieChartIcon, path: OverviewRoutes.createBudget, show: budgetingEnabled && budgeting.canEdit },
      ].filter((action) => canAct && action.show && go.canNavigateTo(action.path)),
    [budgeting.canEdit, budgetingEnabled, canAct, finance.canEdit, go, isParish, pledgesEnabled],
  )

  if (actions.length === 0) {
    return <EmptyState surface={false} compact title={t("widgets.quickActions.empty.title")} />
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {actions.map((action) => (
        <button
          key={action.key}
          type="button"
          onClick={() => go(action.path)}
          className="flex items-center gap-2 rounded-full border border-border bg-background px-2.5 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary motion-reduce:transition-none"
        >
          <action.icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="truncate">{t(`widgets.quickActions.items.${action.key}`)}</span>
        </button>
      ))}
    </div>
  )
}
